import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PageFooter from '../components/PageFooter';
import InstructionsModal from '../components/InstructionsModal';
import { useSkinAnalysisPlan } from '../context/SkinAnalysisPlanContext';
import { initializeSession, clearSession, getSessionId, getSessionIdFromUrl, pathWithSessionId } from '../utils/sessionManager';
import { saveUserInfo } from '../utils/dataSaver';
import { clearCache } from '../utils/dataCache';
import { useSkinSession } from '../context/SkinSessionContext';
import { SESSION_APP_DATA_BASE } from '../config/sessionAppData';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';
import './UserInfoPage.css';
import '../styles/PageBackground.css';
import '../styles/ActionButtons.css';
import SkinTypeCarousel from '../components/SkinTypeCarousel';

const genderImageFile = (g) => (g === 'Transgender' ? 'trans.png' : `${(g || '').toLowerCase()}.png`);
const CONTACT_OPTIONS_DEFAULTS = {};
const CONTACT_OPTIONS_SESSION_PREFIX = 'skin_contact_options_';
const OTP_OPTIONS_DEFAULTS = { enabled: false, domain: '' };

function getContactOptionsStorageKey(token, sessionId) {
  const t = typeof token === 'string' ? token.trim() : '';
  if (t) return `${CONTACT_OPTIONS_SESSION_PREFIX}${t}`;
  const sid = typeof sessionId === 'string' ? sessionId.trim() : '';
  if (sid) return `${CONTACT_OPTIONS_SESSION_PREFIX}sid_${sid}`;
  return null;
}

function normalizeDomain(value) {
  const raw = typeof value === 'string' ? value.trim() : '';
  return raw ? raw.replace(/\/+$/, '') : '';
}

function normalizeOtpEnabled(value) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  const lower = String(value ?? '').toLowerCase().trim();
  return lower === 'true' || lower === '1' || lower === 'yes' || lower === 'on';
}

function normalizeContactFieldKey(field) {
  const s = typeof field === 'object' && field !== null
    ? (field.key ?? field.name ?? field.id ?? '')
    : String(field ?? '');
  const lower = String(s).toLowerCase().trim();
  if (lower === 'phone' || lower === 'mobile_number') return 'mobileNumber';
  if (lower === 'instagram' || lower === 'instagram_id') return 'instagramId';
  return lower || s;
}

function getContactOptionsFromQuestionnaire(questions) {
  if (!Array.isArray(questions) || questions.length === 0) {
    return CONTACT_OPTIONS_DEFAULTS;
  }
  const contactQuestion = questions.find((q) => {
    const type = String(q?.type || '').toLowerCase().trim();
    const label = String(q?.label || '').toLowerCase().trim();
    const key = String(q?.key || '').toLowerCase().trim();
    if (key === 'no') return false;
    return type === 'contact' || label === 'contact details' || label === 'contact detail';
  });
  if (!contactQuestion) {
    return CONTACT_OPTIONS_DEFAULTS;
  }
  if (contactQuestion.options && typeof contactQuestion.options === 'object' && !Array.isArray(contactQuestion.options)) {
    return contactQuestion.options;
  }
  if (Array.isArray(contactQuestion.options)) {
    return Object.fromEntries(
      contactQuestion.options
        .map((field) => normalizeContactFieldKey(field))
        .filter(Boolean)
        .map((field) => [field, true])
    );
  }
  return CONTACT_OPTIONS_DEFAULTS;
}

function getOtpConfigFromQuestionnaire(questions, questionnairePayload = {}) {
  if (!Array.isArray(questions) || questions.length === 0) {
    const payloadDomain = normalizeDomain(questionnairePayload?.domain || questionnairePayload?.data?.domain);
    return { ...OTP_OPTIONS_DEFAULTS, ...(payloadDomain ? { domain: payloadDomain } : {}) };
  }

  const otpQuestion = questions.find((q) => {
    const key = String(q?.key || '').toLowerCase().trim();
    const type = String(q?.type || '').toLowerCase().trim();
    const label = String(q?.label || '').toLowerCase().trim();
    if (key === 'no') return false;
    return type === 'toggle' && (label === 'otp' || key === 'otp');
  });

  const payloadDomain = normalizeDomain(questionnairePayload?.domain || questionnairePayload?.data?.domain);
  if (!otpQuestion) {
    return { ...OTP_OPTIONS_DEFAULTS, ...(payloadDomain ? { domain: payloadDomain } : {}) };
  }

  const enabled = normalizeOtpEnabled(otpQuestion?.options);
  const otpDomain = normalizeDomain(
    otpQuestion?.domain || otpQuestion?.options?.domain || payloadDomain
  );
  return {
    enabled,
    domain: otpDomain,
  };
}

const UserInfoPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, sessionLandingPath } = useSkinSession();
  useSkinAnalysisPlan();
  const userInfoFromState = location.state?.userInfo || null;
  const [gender, setGender] = useState(userInfoFromState?.gender || '');
  const [skinType, setSkinType] = useState(userInfoFromState?.skinType || null);
  const [age, setAge] = useState(userInfoFromState?.age ?? null);
  const [isAgeInputActive, setIsAgeInputActive] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const sessionIdRef = useRef(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const [questionnaire, setQuestionnaire] = useState(null);
  const [questionnaireLoading, setQuestionnaireLoading] = useState(true);
  const [questionnaireError, setQuestionnaireError] = useState(null);
  const [dynamicAnswers, setDynamicAnswers] = useState({});
  const [contactOptionsFromQuestionnaire, setContactOptionsFromQuestionnaire] = useState(CONTACT_OPTIONS_DEFAULTS);
  const [otpConfigFromQuestionnaire, setOtpConfigFromQuestionnaire] = useState(OTP_OPTIONS_DEFAULTS);

  const getActiveQuestion = (label) => {
    if (!Array.isArray(questionnaire) || !label) return null;
    const wanted = String(label).toLowerCase().trim();
    return questionnaire.find((q) => {
      const key = String(q?.key || '').toLowerCase().trim();
      if (key === 'no') return false;
      return String(q?.label || '').toLowerCase().trim() === wanted;
    }) || null;
  };

  const genders = ['Male', 'Female', 'Transgender'];


  // Fetch questionnaire when token is available
  useEffect(() => {
    if (!token) {
      setQuestionnaireLoading(false);
      return;
    }
    let cancelled = false;
    setQuestionnaireLoading(true);
    setQuestionnaireError(null);
    (async () => {
      try {
        const res = await fetchWithTimeout(
          `${SESSION_APP_DATA_BASE}/${encodeURIComponent(token)}/questionnaire`,
          { timeoutMs: 20000 }
        );
        if (!res.ok) throw new Error(`Questionnaire ${res.status}`);
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        const questions = data?.questions ?? data?.data?.questions ?? (Array.isArray(data) ? data : []);
        const normalizedQuestions = Array.isArray(questions) ? questions : [];
        setQuestionnaire(normalizedQuestions);
        const parsedContactOptions = getContactOptionsFromQuestionnaire(normalizedQuestions);
        const parsedOtpConfig = getOtpConfigFromQuestionnaire(normalizedQuestions, data);
        setContactOptionsFromQuestionnaire(parsedContactOptions);
        setOtpConfigFromQuestionnaire(parsedOtpConfig);
        setQuestionnaireLoading(false);
      } catch (err) {
        if (!cancelled) {
          setQuestionnaireError(err?.message || 'Failed to load questionnaire');
          setQuestionnaire(null);
          setContactOptionsFromQuestionnaire(CONTACT_OPTIONS_DEFAULTS);
          setOtpConfigFromQuestionnaire(OTP_OPTIONS_DEFAULTS);
          setQuestionnaireLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Sync dynamic answers to gender/skinType/age when questionnaire is used
  const effectiveGender = questionnaire?.length ? (dynamicAnswers['Gender'] ?? gender) : gender;
  const effectiveSkinType = questionnaire?.length ? (dynamicAnswers['Skin Type'] ?? skinType) : skinType;
  const effectiveAge = questionnaire?.length ? (dynamicAnswers['Age'] ?? age) : age;
  const genderQuestion = getActiveQuestion('Gender');
  const skinTypeQuestion = getActiveQuestion('Skin Type');
  const ageQuestion = getActiveQuestion('Age');
  const isGenderRequired = !questionnaire?.length || (genderQuestion ? genderQuestion.required !== false : false);
  const isSkinTypeRequired = !questionnaire?.length || (skinTypeQuestion ? skinTypeQuestion.required !== false : false);
  const isAgeRequired = !questionnaire?.length || (ageQuestion ? ageQuestion.required !== false : false);
  const isAgeValueValid =
    effectiveAge != null &&
    !isNaN(Number(effectiveAge)) &&
    Number(effectiveAge) >= 14 &&
    Number(effectiveAge) <= 75;
  const demographicsValid =
    (!isGenderRequired || Boolean(effectiveGender)) &&
    (!isSkinTypeRequired || Boolean(effectiveSkinType)) &&
    (!isAgeRequired || isAgeValueValid);

  const setAnswerByLabel = (label, value) => {
    setDynamicAnswers((prev) => ({ ...prev, [label]: value }));
    if (label === 'Gender') setGender(value);
    if (label === 'Skin Type') setSkinType(value);
    if (label === 'Age') setAge(value);
  };

  // Session ID: state (from previous page) > URL (?session_id=) > localStorage. Fallback to URL keeps flow working when state is lost.
  useEffect(() => {
    const fromState = location.state?.sessionId;
    const fromUrl = getSessionIdFromUrl(location);
    const fromStorage = getSessionId();
    const sessionIdFromLanding = fromState || fromUrl || fromStorage;
    if (sessionIdFromLanding) {
      sessionIdRef.current = sessionIdFromLanding;
      setSessionId(sessionIdFromLanding);
      try {
        localStorage.setItem('skin_detection_session_id', sessionIdFromLanding);
      } catch (e) { }
      if (fromState || fromUrl) return;
    }
    const isFromLanding = !location.state?.preserveSession;
    if (isFromLanding && !fromUrl) {
      const prevSessionId = getSessionId();
      if (prevSessionId) {
        clearCache(prevSessionId);
      }
      clearSession();
    }
  }, [location]);

  // When all fields are empty (no questions or every question has empty options), skip form and open disclaimer modal
  const skipUserInfoRef = useRef(false);
  const allFieldsEmpty = Array.isArray(questionnaire) && (
    questionnaire.length === 0 ||
    questionnaire.every((q) => !Array.isArray(q?.options) || q.options.length === 0)
  );
  useEffect(() => {
    if (!token || questionnaireLoading || !allFieldsEmpty || skipUserInfoRef.current) return;
    skipUserInfoRef.current = true;
    const run = async () => {
      const API_BASE_URL = process.env.REACT_APP_API_URL || '';
      let sid = sessionIdRef.current || sessionId;
      if (!sid) {
        try {
          sid = await initializeSession(API_BASE_URL, token ? { app_session_token: token } : {});
          sessionIdRef.current = sid;
          setSessionId(sid);
          if (sid) try { localStorage.setItem('skin_detection_session_id', sid); } catch (_) {}
        } catch (_) {}
      }
      setShowInstructions(true);
    };
    run();
  }, [token, questionnaireLoading, allFieldsEmpty, sessionId]);

  const handleContinue = async () => {
    const g = effectiveGender;
    const s = effectiveSkinType;
    const a = effectiveAge;
    const valid = demographicsValid;
    if (!valid) return;

    setIsProcessing(true);
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || '';
      let sid = sessionIdRef.current || sessionId;
      if (!sid) {
        sid = await initializeSession(API_BASE_URL, token ? { app_session_token: token } : {});
        sessionIdRef.current = sid;
        setSessionId(sid);
      }
      if (sid) {
        const deviceInfo = location.state?.deviceInfo || null;
        saveUserInfo(sid, { gender: g, skinType: s, age: a, ...(questionnaire?.length ? dynamicAnswers : {}) }, deviceInfo, token || null);
      }
    } catch (error) {
      // Continue anyway
    }
    setShowInstructions(true);
  };

  const handleInstructionsContinue = () => {
    setShowInstructions(false);
    setIsProcessing(false);
    const sidToPass = sessionIdRef.current || sessionId;
    const contactOptionsKey = getContactOptionsStorageKey(token, sidToPass);
    if (contactOptionsKey) {
      try {
        sessionStorage.setItem(contactOptionsKey, JSON.stringify({
          contactOptions: contactOptionsFromQuestionnaire || {},
          otpConfig: otpConfigFromQuestionnaire || OTP_OPTIONS_DEFAULTS,
        }));
      } catch (_) {}
    }
    const path = pathWithSessionId('/capture', sidToPass);
    navigate(path, {
      state: {
        gender: effectiveGender,
        skinType: effectiveSkinType,
        age: effectiveAge,
        contactFields: contactOptionsFromQuestionnaire,
        otpConfig: otpConfigFromQuestionnaire,
        sessionId: sidToPass,
        ...(token && { token })
      }
    });
  };

  const handleCloseInstructions = () => {
    setShowInstructions(false);
    setIsProcessing(false);
  };

  const handleBack = () => {
    navigate(sessionLandingPath);
  };

  const handleHome = () => {
    navigate(sessionLandingPath);
  };

  return (
    <div className="user-info-page-shell page-bg" id="user-info-page-shell">
      {/* Navigation Buttons */}
      <div className="user-info-page-nav" id="user-info-page-nav">
        <button
          type="button"
          className="user-info-page-back-button"
          onClick={handleBack}
          aria-label="Go back"
        >
          ←
        </button>
        <button
          type="button"
          className="user-info-page-home-button"
          onClick={handleHome}
          aria-label="Go to home"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
        </button>
      </div>

      <main className="user-info-page-main" id="user-info-page-main">
        <div className="user-info-page-container" id="user-info-page-container">
          <section className="user-info-page-layout" id="user-info-page-layout">
            <p className="user-info-page-subtitle" id="user-info-page-subtitle">Please provide your information for better analysis</p>

            <div className="user-info-form" id="user-info-form">
              {questionnaireLoading && (
                <div className="user-info-loading-overlay" id="user-info-loading-overlay" aria-busy="true" aria-live="polite">
                  <div className="user-info-loading-content">
                    <div className="loading-spinner" aria-hidden="true" />
                    <p className="loading-text">Loading options…</p>
                  </div>
                </div>
              )}
              {!questionnaireLoading && questionnaireError && !questionnaire?.length && (
                <p className="user-info-page-subtitle user-info-page-subtitle-error">{questionnaireError}</p>
              )}

              <div className={`user-info-form-body ${isProcessing ? 'user-info-form-blocked' : ''}`}>
                {token && allFieldsEmpty ? (
                  <div className="user-info-loading-overlay" id="user-info-skip-overlay" aria-busy="true" aria-live="polite">
                    <div className="user-info-loading-content">
                      <div className="loading-spinner" aria-hidden="true" />
                      <p className="loading-text">Loading…</p>
                    </div>
                  </div>
                ) : questionnaire?.length > 0 ? (
                  <>
                    {[...questionnaire]
                      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                      .map((q, idx) => {
                        if (q.key === 'no') return null;

                        const label = q.label || `Question ${idx + 1}`;
                        const options = Array.isArray(q.options) ? q.options : [];
                        const normalizedType = String(q.type || '').toLowerCase();
                        const isSelectLike = normalizedType === 'multi-select' || normalizedType === 'yes_no' || normalizedType === 'single-select' || normalizedType === 'select';
                        const value = dynamicAnswers[label] ?? (label === 'Gender' ? gender : label === 'Skin Type' ? skinType : label === 'Age' ? age : null);
                        const required = q.required !== false;

                        if (normalizedType === 'number') {
                          const defaultNum = typeof q.options === 'number' ? q.options : 18;
                          const numValue = value !== null && value !== undefined && value !== '' ? Number(value) : null;
                          return (
                            <div key={`${label}-${idx}`} className="user-info-section" id={`user-info-section-${label.replace(/\s+/g, '-')}`}>
                              <label className="user-info-label">
                                {label} {required && <span className="required">*</span>}
                              </label>
                              <div className="user-info-age-selector-container">
                                <div className="user-info-age-selector">
                                  <button
                                    type="button"
                                    className="user-info-age-selector-button user-info-age-decrement"
                                    onClick={() => {
                                      const current = numValue ?? defaultNum;
                                      if (current > 14) setAnswerByLabel(label, current - 1);
                                    }}
                                    aria-label="Decrease"
                                  >−</button>
                                  <div className="user-info-age-display">
                                    {isAgeInputActive && label === 'Age' ? (
                                      <input
                                        type="number"
                                        className="user-info-age-input"
                                        min="14"
                                        max="75"
                                        value={numValue ?? ''}
                                        placeholder="Enter age (14-75)"
                                        autoFocus
                                        onChange={(e) => {
                                          const v = e.target.value;
                                          if (v === '') { setAnswerByLabel(label, null); return; }
                                          const n = parseInt(v, 10);
                                          if (!isNaN(n)) setAnswerByLabel(label, n);
                                        }}
                                        onBlur={() => {
                                          const n = numValue !== null && numValue !== undefined ? Number(numValue) : null;
                                          if (n !== null && (n < 14 || n > 75)) setAnswerByLabel(label, Math.max(14, Math.min(75, n)));
                                          setIsAgeInputActive(false);
                                        }}
                                      />
                                    ) : (
                                      <span
                                        className={`user-info-age-value ${(numValue === null || numValue === undefined) ? 'user-info-age-value-placeholder' : ''}`}
                                        onClick={() => label === 'Age' && setIsAgeInputActive(true)}
                                      >
                                        {numValue !== null && numValue !== undefined ? numValue : 'Select'}
                                      </span>
                                    )}
                                  </div>
                                  <button
                                    type="button"
                                    className="user-info-age-selector-button user-info-age-increment"
                                    onClick={() => {
                                      const current = numValue ?? defaultNum;
                                      if (current < 75) setAnswerByLabel(label, current + 1);
                                    }}
                                    aria-label="Increase"
                                  >+</button>
                                </div>
                              </div>
                            </div>
                          );
                        }

                        if (isSelectLike) {
                          if (options.length === 0) {
                            return (
                              <div key={`${label}-${idx}`} className="user-info-section" id={`user-info-section-${label.replace(/\s+/g, '-')}`}>
                                <label className="user-info-label" id={`user-info-label-${label.replace(/\s+/g, '-')}`}>
                                  {label} {required && <span className="required">*</span>}
                                </label>
                                <input
                                  type="text"
                                  className="user-info-text-input"
                                  value={value ?? ''}
                                  onChange={(e) => setAnswerByLabel(label, e.target.value)}
                                  placeholder={`Enter ${label}`}
                                />
                              </div>
                            );
                          }
                          if (label === 'Skin Type' && options.length > 0) {
                            return (
                              <div key={`${label}-${idx}`} className="user-info-section" id="user-info-section-skin-type">
                                <div className="user-info-row-header">
                                  <label className="user-info-label" id="user-info-label-skin-type">
                                    {label} {required && <span className="required">*</span>}
                                  </label>
                                  <span className="user-info-help-text">Swipe or use arrows to select</span>
                                </div>
                                <div className="user-info-carousel-wrapper">
                                  <SkinTypeCarousel
                                    options={options}
                                    selectedType={value ?? null}
                                    onSelect={(v) => setAnswerByLabel(label, v)}
                                  />
                                </div>
                              </div>
                            );
                          }
                          const isGender = label === 'Gender';
                          return (
                            <div key={`${label}-${idx}`} className="user-info-section" id={`user-info-section-${label.replace(/\s+/g, '-')}`}>
                              <label className="user-info-label" id={`user-info-label-${label.replace(/\s+/g, '-')}`}>
                                {label} {required && <span className="required">*</span>}
                              </label>
                              <div className={`user-info-options ${isGender ? 'user-info-options-gender' : ''}`} id={isGender ? 'user-info-options-gender' : undefined}>
                                {options.map((opt) => (
                                  <button
                                    key={opt}
                                    type="button"
                                    className={`user-info-option ${isGender ? 'user-info-option-gender' : ''} ${value === opt ? 'user-info-option-selected' : ''}`}
                                    id={isGender ? `user-info-option-gender-${String(opt).toLowerCase().replace(/\s+/g, '-')}` : undefined}
                                    onClick={() => setAnswerByLabel(label, opt)}
                                  >
                                    {isGender && (
                                      <img
                                        src={`${process.env.PUBLIC_URL}/gender/${genderImageFile(opt)}`}
                                        alt={opt}
                                        className="user-info-gender-image"
                                      />
                                    )}
                                    <span className="user-info-gender-label">{opt}</span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          );
                        }

                        return null;
                      })}
                    <div className="skin-page-actions" id="user-info-actions">
                      <button
                        type="button"
                        className={`skin-action-button skin-action-button-large skin-action-button-rounded ${!demographicsValid || isProcessing ? 'disabled' : ''} ${isProcessing ? 'user-info-continue-button-processing' : ''}`}
                        id="user-info-continue-button"
                        onClick={handleContinue}
                        disabled={!demographicsValid || isProcessing}
                      >
                        {isProcessing ? 'Processing...' : 'Continue'}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="user-info-section" id="user-info-section-gender">
                      <label className="user-info-label" id="user-info-label-gender">Gender <span className="required">*</span></label>
                      <div className="user-info-options user-info-options-gender" id="user-info-options-gender">
                        {genders.map((g) => (
                          <button
                            key={g}
                            type="button"
                            className={`user-info-option user-info-option-gender ${gender === g ? 'user-info-option-selected' : ''}`}
                            id={`user-info-option-gender-${g.toLowerCase()}`}
                            onClick={() => setGender(g)}
                          >
                            <img src={`${process.env.PUBLIC_URL}/gender/${genderImageFile(g)}`} alt={g} className="user-info-gender-image" />
                            <span className="user-info-gender-label">{g}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="user-info-section" id="user-info-section-skin-type">
                      <div className="user-info-row-header">
                        <label className="user-info-label" id="user-info-label-skin-type">Skin Type <span className="required">*</span></label>
                        <span className="user-info-help-text">Swipe or use arrows to select</span>
                      </div>
                      <div className="user-info-carousel-wrapper">
                        <SkinTypeCarousel selectedType={skinType} onSelect={setSkinType} />
                      </div>
                      {skinType && (
                        <p className="user-info-skin-description">
                          {{ 'Normal': 'Balanced, clear, not sensitive', 'Dry': 'Rough, flaky, feels tight', 'Oily': 'Shiny, enlarged pores, prone to acne', 'Combination': 'Oily T-zone (forehead, nose), dry cheeks', 'Sensitive': 'Redness, itching, burning, reactive' }[skinType]}
                        </p>
                      )}
                    </div>
                    <div className="user-info-section" id="user-info-section-age">
                      <label className="user-info-label" id="user-info-label-age">Age <span className="required">*</span></label>
                      <div className="user-info-age-selector-container">
                        <div className="user-info-age-selector">
                          <button type="button" className="user-info-age-selector-button user-info-age-decrement" onClick={() => { const c = age ?? 22; if (c > 14) setAge(c - 1); }} aria-label="Decrease age">−</button>
                          <div className="user-info-age-display">
                            {isAgeInputActive ? (
                              <input type="number" className="user-info-age-input" min="14" max="75" value={age ?? ''} placeholder="Enter age (14-75)" autoFocus onChange={(e) => { const v = e.target.value; if (v === '') { setAge(null); return; } const n = parseInt(v, 10); if (!isNaN(n)) setAge(n); }} onBlur={() => { const a = age === '' || age === null || age === undefined ? null : Number(age); setAge(a === null || a < 14 || a > 75 ? null : Math.max(14, Math.min(75, a))); setIsAgeInputActive(false); }} onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { setIsAgeInputActive(false); setAge(null); } }} />
                            ) : (
                              <span className={`user-info-age-value ${(age === null || age === undefined) ? 'user-info-age-value-placeholder' : ''}`} onClick={() => setIsAgeInputActive(true)} title="Click to enter age manually">{age !== null && age !== undefined ? age : 'Select age'}</span>
                            )}
                          </div>
                          <button type="button" className="user-info-age-selector-button user-info-age-increment" onClick={() => { const c = age ?? 22; if (c < 75) setAge(c + 1); }} aria-label="Increase age">+</button>
                        </div>
                      </div>
                    </div>
                    <div className="skin-page-actions" id="user-info-actions">
                      <button type="button" className={`skin-action-button skin-action-button-large skin-action-button-rounded ${!demographicsValid || isProcessing ? 'disabled' : ''} ${isProcessing ? 'user-info-continue-button-processing' : ''}`} id="user-info-continue-button" onClick={handleContinue} disabled={!demographicsValid || isProcessing}>
                        {isProcessing ? 'Processing...' : 'Continue'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </section>
        </div>
      </main>

      <PageFooter />

      <InstructionsModal
        isOpen={showInstructions}
        onClose={handleCloseInstructions}
        onContinue={handleInstructionsContinue}
        token={token}
      />
    </div>
  );
};

export default UserInfoPage;

